import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

import acceptTemplate from "./AcceptSystemDocumentTemplateMutation";

import { ContentContainer } from "pstat-anywhere/components/pages";
import AdvancedReportPage from "pstat-anywhere/components/pages/reports/AdvancedReportPage";
import { Bucket } from "pstat-anywhere/components/reports/Buckets";
import { useLabels } from "pstat-anywhere/context_providers/LabelContext";
import titleCase from "pstat-anywhere/utils/titleCase";
import { createErrorMessage } from "pstat-design-system/message/manageMessages";
import LoadingModal from "pstat-design-system/modal/LoadingModal";


const AcceptanceReportPage = ({
  blocSystemDocumentTemplatesAcceptance,
  location,
  variables,
  route,
  router
}) => {
  const { t } = useTranslation();
  const { categoryLabel, documentLabelPlural } = useLabels();
  const [loadingAcceptTemplate, setLoadingAcceptTemplate] = useState(false);
  const templates =
    blocSystemDocumentTemplatesAcceptance?.blocSystemDocumentTemplates;
  const edges = transformEdgesForTable(templates?.edges);
  const data = {
    edges,
    totalCount: templates?.totalCount
  };
  const buckets = blocSystemDocumentTemplatesAcceptance?.buckets;
  const tabs = [
    {
      id: "system_templates_manage",
      textKey: "systemDocumentTemplates.tab",
      path: ""
    }
  ];
  const dueForReviewDays = parseInt(getLastBucketStepKey(buckets), 10);
  const startDraft = templatePk => {
    setLoadingAcceptTemplate(true);
    acceptTemplate(templatePk, (response, error) => {
      setLoadingAcceptTemplate(false);
      const ok = response.acceptSystemDocumentTemplate?.ok;
      const errors = response.acceptSystemDocumentTemplate?.error;
      if (error || errors || !ok) {
        if (errors.category) {
          createErrorMessage(
            t(
              "systemDocumentTemplates.acceptanceReport.startDraftCategoryError",
              { categoryLabel: categoryLabel.toLowerCase() }
            )
          );
        } else if (errors.flow_template) {
          createErrorMessage(
            t(
              "systemDocumentTemplates.acceptanceReport.startDraftWorkflowError"
            )
          );
        } else {
          createErrorMessage(
            t("systemDocumentTemplates.acceptanceReport.startDraftError")
          );
        }
        return;
      }
      if (ok) {
        router.push(response.acceptSystemDocumentTemplate?.redirectPath);
      }
    });
  };
  return (
    <ContentContainer>
      <LoadingModal isOpen={loadingAcceptTemplate} />
      <AdvancedReportPage
        variables={variables}
        data={data}
        route={route}
        router={router}
        location={location}
        tabs={tabs}
        headerText={t("systemDocumentTemplates.acceptanceReport.header", {
          documentLabelPlural: titleCase(documentLabelPlural)
        })}
        noContentMessage={t(
          "systemDocumentTemplates.acceptanceReport.emptyMessage",
          { documentLabelPlural: documentLabelPlural.toLowerCase() }
        )}
        showTabsPanel={true}
        tableType="templatesToAccept"
        showPagination={true}
        showHitsPerPageSelector={true}
        customOnRow={() => {}}
        showActionButtonPanel={true}
        frozenColumnTable={true}
        BucketsComponent={() => <Buckets buckets={buckets} />}
        otherTableProps={{
          startDraft,
          dueForReviewDays
        }}
      />
    </ContentContainer>
  );
};

export default AcceptanceReportPage;

const transformEdgesForTable = edges => {
  return edges?.map(edge => {
    const { systemDocumentTemplate, document, publishDate } = edge.node;
    return {
      node: {
        ...systemDocumentTemplate,
        pk: document ? document.pk : systemDocumentTemplate.pk,
        status: document ? document.status : "DRAFT_NOT_STARTED",
        publishDate,
        hasDocument: !!document
      }
    };
  });
};

export const Buckets = ({ buckets }) => {
  const { t } = useTranslation();
  const labels = useLabels();
  let lastStepKey = getLastBucketStepKey(buckets);
  const revisionDateLabel = titleCase(labels.revisionDateLabel);
  return (
    <Fragment>
      <Bucket
        label={t("systemDocumentTemplates.acceptanceReport.lessThanBucket", {
          revisionDateLabel,
          count: 30
        })}
        count={buckets?.["30"]}
        color="#00A429"
      />
      <Bucket
        label={t("systemDocumentTemplates.acceptanceReport.lessThanBucket", {
          revisionDateLabel,
          count: 60
        })}
        count={buckets?.["60"]}
        color="#B55B00"
      />
      <Bucket
        label={t("systemDocumentTemplates.acceptanceReport.lessThanBucket", {
          revisionDateLabel,
          count: lastStepKey ? lastStepKey : ""
        })}
        count={buckets?.[lastStepKey]}
        color="#B55B00"
      />
      <Bucket
        label={t("systemDocumentTemplates.acceptanceReport.greaterThanBucket", {
          revisionDateLabel,
          count: lastStepKey ? lastStepKey : ""
        })}
        count={buckets?.overdue}
        color="#BB0000"
      />
    </Fragment>
  );
};

export const getLastBucketStepKey = buckets => {
  if (buckets) {
    const keys = Object.keys(buckets);
    return keys[3];
  }
  return null;
};
